import { useState, useCallback, useEffect, useRef, useMemo, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IGetRowsParams,
  type ValueFormatterParams,
} from 'ag-grid-community';
import { RefreshCw, ChevronDown, ChevronUp, DollarSign, Info } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { useParamStore } from '../../hooks/useParamStore';
import { emptyFormatter } from '../../lib/gridUtils';
import { reportDatasourceFailure } from '../../lib/agGridErrorTracker';
import type { Distributor } from '../../types';

ModuleRegistry.registerModules([AllCommunityModule]);

// ── Local types ─────────────────────────────────────────────────────────────

interface CreditSummary {
  credit_limit: number | null;
  soft_reserve: number | null;
  available: number | null;
  utilization_pct: number | null;
  hard_draw_mtd?: number | null;
}

interface CashPosition {
  totalCommitted: number;
  hardDrawMtd: number;
  projectedPayouts: number;
}

interface LedgerRow {
  id: string;
  created_at: string | null;
  event_type: string | null;
  po_id: string | null;
  amount: number | null;
  balance_after: number | null; // FN-2: DERIVED at read by the credit_ledger_balanced view (SUM(amount) OVER), not stored
  notes: string | null;
  po_number: string | null; // FN-2: flat from the view's LEFT JOIN (no PostgREST embed)
}

type GenericSupabaseRpc = (
  name: string,
  args: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Formatting ──────────────────────────────────────────────────────────────

function fmtCurrency(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '\u2014';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function relativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ── Event badge ─────────────────────────────────────────────────────────────

const EVENT_COLORS: Record<string, string> = {
  soft_reserve: 'bg-warning/15 text-warning',
  hard_draw: 'bg-danger/15 text-danger',
  reserve_release: 'bg-success/15 text-success',
  credit_adjustment: 'bg-info/15 text-info',
  payment: 'bg-success/15 text-success',
};

const EVENT_LABELS: Record<string, string> = {
  soft_reserve: 'Soft Reserve',
  hard_draw: 'Hard Draw',
  reserve_release: 'Reserve Release',
  credit_adjustment: 'Adjustment',
  payment: 'Payment',
};

interface EventBadgeProps {
  type: string;
}

function EventBadge({ type }: EventBadgeProps): JSX.Element {
  return (
    <span
      className={clsx(
        'px-1.5 py-0.5 rounded text-2xs font-medium',
        EVENT_COLORS[type] || 'bg-surface-3 text-ink-muted'
      )}
    >
      {EVENT_LABELS[type] || type}
    </span>
  );
}

// ── Distributor Credit Card ─────────────────────────────────────────────────

interface CreditCardProps {
  distributor: Distributor;
  summary: CreditSummary | undefined;
  expanded: boolean;
  onToggle: () => void;
}

function CreditCard({ distributor, summary, expanded, onToggle }: CreditCardProps): JSX.Element {
  const paramStore = useParamStore();
  const limit = summary?.credit_limit ?? 0;
  const soft = summary?.soft_reserve ?? 0;
  const available = summary?.available ?? 0;
  const utilPct = summary?.utilization_pct ?? 0;

  const warningThreshold = Number(
    paramStore.resolveKey('credit_util_warning_pct')?.param.value ?? 50
  );
  const dangerThreshold = Number(
    paramStore.resolveKey('credit_util_danger_pct')?.param.value ?? 80
  );
  const availableDangerRatio =
    Number(paramStore.resolveKey('credit_available_danger_pct')?.param.value ?? 20) / 100;

  // TODO: extract soft-reserve 70% threshold to a dedicated param (separate from the credit-util/available trio)
  const softColor = limit > 0 && soft / limit > 0.7 ? 'text-warning' : 'text-ink-primary';
  const availColor =
    limit > 0 && available / limit < availableDangerRatio ? 'text-danger' : 'text-success';

  return (
    <div className="bg-surface-1 border border-border rounded-lg">
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-ink-bright">{distributor.name}</h3>
            <span
              className={clsx(
                'px-1.5 py-0.5 rounded text-2xs font-medium',
                distributor.is_active ? 'bg-success/15 text-success' : 'bg-surface-3 text-ink-muted'
              )}
            >
              {distributor.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <button
            onClick={onToggle}
            className="arbi-btn-ghost text-xs px-2 py-1 flex items-center gap-1"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            View Ledger
          </button>
        </div>

        {/* Metric row */}
        <div className="grid grid-cols-4 gap-4">
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">Credit Limit</p>
            <p className="text-sm font-semibold font-mono text-ink-primary">{fmtCurrency(limit)}</p>
          </div>
          <div className="group relative">
            <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1 flex items-center gap-1">
              Soft Reserve <Info size={10} className="text-ink-muted" />
            </p>
            <p className={clsx('text-sm font-semibold font-mono', softColor)}>
              {fmtCurrency(soft)}
            </p>
            <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-surface-4 text-2xs text-ink-primary rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Active POs awaiting receipt — approved through partially received
            </div>
          </div>
          <div className="group relative">
            <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1 flex items-center gap-1">
              Available <Info size={10} className="text-ink-muted" />
            </p>
            <p className={clsx('text-sm font-semibold font-mono', availColor)}>
              {fmtCurrency(available)}
            </p>
            <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-surface-4 text-2xs text-ink-primary rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              Credit limit minus all active soft reserves
            </div>
          </div>
          <div>
            <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">Utilization</p>
            <p
              className={clsx(
                'text-sm font-semibold font-mono',
                utilPct > dangerThreshold
                  ? 'text-danger'
                  : utilPct > warningThreshold
                    ? 'text-warning'
                    : 'text-ink-primary'
              )}
            >
              {utilPct}%
            </p>
            <div className="w-full h-1.5 bg-surface-3 rounded-full mt-1.5">
              <div
                className={clsx(
                  'h-full rounded-full transition-all',
                  utilPct > dangerThreshold
                    ? 'bg-danger'
                    : utilPct > warningThreshold
                      ? 'bg-warning'
                      : 'bg-success'
                )}
                style={{ width: `${Math.min(utilPct, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Expandable ledger */}
      {expanded && <LedgerGrid distributorId={distributor.id} />}
    </div>
  );
}

// ── Ledger Grid ─────────────────────────────────────────────────────────────

interface LedgerGridProps {
  distributorId: string;
}

function LedgerGrid({ distributorId }: LedgerGridProps): JSX.Element {
  const navigate = useNavigate();
  const gridApiRef = useRef<GridApi<LedgerRow> | null>(null);

  const getRowsFn = useCallback(
    async (params: IGetRowsParams): Promise<void> => {
      const { startRow, endRow, successCallback, failCallback } = params;
      try {
        // FN-2: read the credit_ledger_balanced VIEW — balance_after is DERIVED
        // (SUM(amount) OVER the total order), no longer a stored column that forks
        // under concurrency (DD-03/R1-F6a). po_number is flat from the view's join.
        const query = supabase
          .from('credit_ledger_balanced')
          .select('*', { count: 'exact' })
          .eq('distributor_id', distributorId)
          .order('created_at', { ascending: false })
          .range(startRow, endRow - 1);

        const { data, count, error } = await query;
        if (error) throw error;
        successCallback((data as unknown as LedgerRow[]) || [], count || 0);
      } catch (e) {
        reportDatasourceFailure(
          'Finance — Credit Ledger',
          { rpcName: 'credit_ledger', params: { distributorId, startRow, endRow } },
          e,
          () => failCallback()
        );
      }
    },
    [distributorId]
  );

  const onGridReady = useCallback(
    (params: GridReadyEvent<LedgerRow>): void => {
      gridApiRef.current = params.api;
      params.api.setGridOption('datasource', { getRows: getRowsFn });
    },
    [getRowsFn]
  );

  const columnDefs = useMemo<ColDef<LedgerRow>[]>(
    () =>
      [
        {
          headerName: 'Date',
          field: 'created_at',
          width: 130,
          valueFormatter: (p: ValueFormatterParams<LedgerRow>) =>
            relativeTime(p.value as string | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
            color: 'var(--ink-muted)',
          },
        },
        {
          headerName: 'Event',
          field: 'event_type',
          flex: 1,
          minWidth: 140,
          cellRenderer: (p: ICellRendererParams<LedgerRow>) =>
            p.data?.event_type ? <EventBadge type={p.data.event_type} /> : null,
          cellStyle: { display: 'flex', alignItems: 'center' },
        },
        {
          headerName: 'PO Number',
          field: 'po_id',
          width: 130,
          valueFormatter: (p: ValueFormatterParams<LedgerRow>) => p.data?.po_number || '\u2014',
          cellRenderer: (p: ICellRendererParams<LedgerRow>) => {
            const poNum = p.data?.po_number;
            const poId = p.data?.po_id;
            if (!poNum) return <span className="text-ink-muted">{'\u2014'}</span>;
            return (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (poId) navigate(`/purchasing/purchase-orders/${poId}`);
                }}
                className="font-mono text-accent hover:text-accent-hover transition-colors cursor-pointer"
              >
                {poNum}
              </button>
            );
          },
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'JetBrains Mono', monospace",
          },
        },
        {
          headerName: 'Amount',
          field: 'amount',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<LedgerRow>) =>
            fmtCurrency(p.value as number | null),
          cellStyle: (p) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
            color: Number(p.value) > 0 ? 'var(--danger)' : 'var(--success)',
          }),
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Balance After',
          field: 'balance_after',
          width: 120,
          valueFormatter: (p: ValueFormatterParams<LedgerRow>) =>
            fmtCurrency(p.value as number | null),
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            fontFamily: "'JetBrains Mono', monospace",
          },
          headerClass: 'ag-right-aligned-header',
        },
        {
          headerName: 'Notes',
          field: 'notes',
          width: 150,
          cellStyle: { display: 'flex', alignItems: 'center', color: 'var(--ink-muted)' },
        },
      ] as ColDef<LedgerRow>[],
    [navigate]
  );

  const defaultColDef = useMemo<ColDef<LedgerRow>>(
    () => ({
      sortable: true,
      resizable: true,
      sortingOrder: ['asc', 'desc', null],
      unSortIcon: true,
      headerClass: 'ag-left-aligned-header',
      cellStyle: { textAlign: 'left' },
      valueFormatter: emptyFormatter,
    }),
    []
  );

  const gridTheme = useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: 'var(--surface-0)',
        headerBackgroundColor: 'var(--surface-1)',
        headerTextColor: 'var(--ink-muted)',
        foregroundColor: 'var(--ink-primary)',
        borderColor: 'var(--border)',
        rowHoverColor: 'var(--surface-3)',
        headerFontSize: 10,
        fontSize: 12,
        spacing: 4,
      }),
    []
  );

  return (
    <div className="border-t border-border p-2" style={{ height: 300 }}>
      <AgGridReact
        theme={gridTheme}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowModelType="infinite"
        cacheBlockSize={100}
        maxBlocksInCache={20}
        onGridReady={onGridReady}
        animateRows={false}
        headerHeight={36}
        rowHeight={42}
        suppressCellFocus={true}
        suppressHorizontalScroll={true}
        noRowsOverlayComponent={() => (
          <div className="flex flex-col items-center justify-center h-full">
            <p className="text-sm text-ink-muted">No ledger entries yet</p>
          </div>
        )}
      />
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export default function FinanceCreditCash(): JSX.Element {
  const { distributors } = useApp();
  const [summaries, setSummaries] = useState<Record<string, CreditSummary>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [cashPosition, setCashPosition] = useState<CashPosition>({
    totalCommitted: 0,
    hardDrawMtd: 0,
    projectedPayouts: 0,
  });

  const fetchSummaries = useCallback(async (): Promise<void> => {
    setLoading(true);
    const results: Record<string, CreditSummary> = {};
    for (const d of distributors) {
      const { data, error } = await callRpc('get_credit_summary', { p_distributor_id: d.id });
      if (!error && data) results[d.id] = data as CreditSummary;
    }
    setSummaries(results);

    // Cash position
    let totalCommitted = 0;
    let hardDrawMtd = 0;
    for (const s of Object.values(results)) {
      totalCommitted += Number(s.soft_reserve) || 0;
      hardDrawMtd += Number(s.hard_draw_mtd) || 0;
    }

    // Projected payouts
    const { data: payoutRows } = await supabase
      .from('payouts')
      .select('net_amount, status')
      .in('status', ['pending', 'received']);
    const rows = (payoutRows ?? []) as Array<{
      net_amount: number | null;
      status: string | null;
    }>;
    const projectedPayouts = rows.reduce((sum, r) => sum + (Number(r.net_amount) || 0), 0);

    setCashPosition({ totalCommitted, hardDrawMtd, projectedPayouts });
    setLoading(false);
  }, [distributors]);

  useEffect(() => {
    if (distributors.length > 0) fetchSummaries();
  }, [distributors, fetchSummaries]);

  const netCash = cashPosition.projectedPayouts - cashPosition.totalCommitted;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-5xl mx-auto p-5 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-ink-bright">Credit & Cash</h1>
            <p className="text-xs text-ink-muted mt-1">
              Credit utilization and cash position across all distributors
            </p>
          </div>
          <button
            onClick={fetchSummaries}
            className="arbi-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5"
            disabled={loading}
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>

        {/* Section 1: Distributor credit cards */}
        <section>
          <h2 className="text-sm font-semibold text-ink-bright mb-3">
            Credit Summary by Distributor
          </h2>
          <div className="space-y-4">
            {distributors.length === 0 ? (
              <div className="bg-surface-1 border border-border rounded-lg p-8 text-center">
                <DollarSign size={48} className="text-ink-muted mx-auto mb-3" />
                <p className="text-sm text-ink-muted">No active distributors</p>
              </div>
            ) : (
              distributors.map((d) => (
                <CreditCard
                  key={d.id}
                  distributor={d}
                  summary={summaries[d.id]}
                  expanded={expandedId === d.id}
                  onToggle={() => setExpandedId((prev) => (prev === d.id ? null : d.id))}
                />
              ))
            )}
          </div>
        </section>

        {/* Section 3: Cash Position Summary */}
        <section>
          <h2 className="text-sm font-semibold text-ink-bright mb-3">Cash Position</h2>
          <div className="bg-surface-1 border border-border rounded-lg p-4">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">
                  Total Committed
                </p>
                <p className="text-sm font-semibold font-mono text-warning">
                  {fmtCurrency(cashPosition.totalCommitted)}
                </p>
                <p className="text-2xs text-ink-muted mt-0.5">Active soft reserves</p>
              </div>
              <div>
                <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">
                  Hard Draws MTD
                </p>
                <p className="text-sm font-semibold font-mono text-danger">
                  {fmtCurrency(cashPosition.hardDrawMtd)}
                </p>
                <p className="text-2xs text-ink-muted mt-0.5">Received POs this month</p>
              </div>
              <div>
                <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">
                  Projected Payouts
                </p>
                <p className="text-sm font-semibold font-mono text-info">
                  {fmtCurrency(cashPosition.projectedPayouts)}
                </p>
                <p className="text-2xs text-ink-muted mt-0.5">Pending + received</p>
              </div>
              <div>
                <p className="text-2xs text-ink-muted uppercase tracking-wider mb-1">
                  Net Position
                </p>
                <p
                  className={clsx(
                    'text-sm font-semibold font-mono',
                    netCash >= 0 ? 'text-success' : 'text-danger'
                  )}
                >
                  {fmtCurrency(netCash)}
                </p>
                <p className="text-2xs text-ink-muted mt-0.5">Payouts minus committed</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
